import { CATEGORY_LABELS, visibleCategory } from './finance';
import { naturalezaDe } from './naturaleza';
import { pareceInversion } from './inversiones';
import { CATEGORIAS_SIN_FACTURA, grupoDeGasto, sumarDias, totalDeGasto, type GrupoGasto } from './gastos';
import { parseExpenseCategory, parseImportBoolean, parseImportPaymentType, type ExpenseImportItem } from './importExpenses';
import { detectDayFirst, normalizeText, parseImportDate } from './importer';
import {
  etiquetaDeCategoria,
  filasDePlantilla,
  leerCategoriaPlantilla,
  type CategoriaLeida,
  type PlantillaDetectada,
} from './importPlantillas';
import { adivinarCategoria } from './receipts';
import { SHARED_LOCATION_ID, type ExpenseCategory, type PurchaseDocumentType, type TenantSettings } from './types';
import { expenseInputSchema, type ExpenseInput } from './validation';

/**
 * Importar gastos (rediseño de Alex, 11 oct 2026; bloque G, 14 oct 2026).
 *
 * UN solo camino para cualquier Excel —el de la plantilla, el del contador o
 * el del banco—, que acaba siempre en «Antes de guardar»:
 *
 *  1. Las columnas se buscan por nombre y por contenido (`entenderColumnas`).
 *     Si no hay total pero hay base + IVA, total = base + IVA; varias
 *     columnas de IVA se suman; lo que no sirve se dice y se ignora. Si no
 *     aparecen la fecha o el total, se pregunta ANTES de seguir.
 *  2. Cada fila pasa por `revisarGastos`: lista para guardar, o «a revisar»
 *     (repetida, sin categoría segura, fecha futura, total negativo, parece
 *     una inversión…). Las dudosas NO bloquean: se guardan «Por confirmar» y
 *     no cuentan en el resultado hasta que alguien las mira.
 */

const round2 = (n: number): number => Math.round(n * 100) / 100;

/* ------------------------------------------------------------------ */
/* Números                                                             */
/* ------------------------------------------------------------------ */

/**
 * Dinero de una celda en cualquier formato: «1.800,00», «1,800.00», «$ 1,800»,
 * «1800», «(25,00)». Con UN solo separador seguido de exactamente tres
 * cifras («1.800», «1,800») es separador de miles, no decimales: un gasto de
 * 1,8 no existe y uno de 1.800 sí (antes se leía 1,8).
 */
export function parseImportMoney(raw: string | number | undefined | null): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  let s = String(raw).replace(/[€$\s ]/g, '').replace(/^(MXN|USD|EUR|ARS|CLP|COP|PEN)/i, '').replace(/(MXN|USD|EUR|ARS|CLP|COP|PEN)$/i, '');
  if (s === '' || s === '-') return undefined;
  let negativo = false;
  if (/^\(.*\)$/.test(s)) {
    negativo = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith('-')) {
    negativo = !negativo;
    s = s.slice(1);
  }
  if (s.endsWith('-')) {
    negativo = !negativo;
    s = s.slice(0, -1);
  }
  if (!/^[\d.,]+$/.test(s)) return undefined;
  const puntos = (s.match(/\./g) ?? []).length;
  const comas = (s.match(/,/g) ?? []).length;
  if (puntos > 0 && comas > 0) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (comas > 0) {
    s = comas > 1 || /^[1-9]\d{0,2},\d{3}$/.test(s) ? s.replace(/,/g, '') : s.replace(',', '.');
  } else if (puntos > 0) {
    if (puntos > 1 || /^[1-9]\d{0,2}\.\d{3}$/.test(s)) s = s.replace(/\./g, '');
  }
  const value = Number(s);
  if (!Number.isFinite(value)) return undefined;
  return negativo ? -value : value;
}

/* ------------------------------------------------------------------ */
/* Fechas                                                              */
/* ------------------------------------------------------------------ */

/** Fecha de una celda: ISO, dd/mm/aaaa, dd-mm-aa o número de serie de Excel. */
export function fechaDeCelda(raw: string, diaPrimero: boolean): string | undefined {
  const s = String(raw ?? '').trim();
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(s)) * 86_400_000);
    return d.toISOString().slice(0, 10);
  }
  return parseImportDate(s, diaPrimero);
}

const FECHA_BARRAS = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2,4})$/;

/**
 * ¿Todo el archivo es ambiguo? (03/04: ¿3 de abril o 4 de marzo?). Ninguna
 * fecha lo resuelve —ningún número pasa de 12— y alguna tiene día y mes
 * distintos. Entonces se usa el formato del país y se pregunta una vez.
 */
export function fechasAmbiguas(valores: Array<string | undefined>): boolean {
  let hayDudosa = false;
  for (const v of valores) {
    const m = String(v ?? '').trim().match(FECHA_BARRAS);
    if (!m) continue;
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a > 12 || b > 12) return false;
    if (a !== b) hayDudosa = true;
  }
  return hayDudosa;
}

/* ------------------------------------------------------------------ */
/* Columnas                                                            */
/* ------------------------------------------------------------------ */

export type CampoGasto =
  | 'fecha'
  | 'proveedor'
  | 'total'
  | 'iva'
  | 'base'
  | 'ivaPct'
  | 'categoria'
  | 'pagado'
  | 'vence'
  | 'numero'
  | 'concepto'
  | 'tipo'
  | 'local';

export type DestinoColumna = CampoGasto | 'ignorar';

export const CAMPO_GASTO_LABELS: Record<DestinoColumna, string> = {
  fecha: 'Fecha',
  proveedor: 'Proveedor',
  total: 'Total',
  iva: 'IVA',
  base: 'Sin IVA (base)',
  ivaPct: 'IVA %',
  categoria: 'Categoría',
  pagado: '¿Ya lo pagaste?',
  vence: 'Vence el',
  numero: 'Nº de factura',
  concepto: 'Concepto',
  tipo: '¿Qué te dieron?',
  local: 'Local',
  ignorar: 'No la uso',
};

/** Orden en que se enseñan «Así entendí tus columnas». */
export const CAMPOS_GASTO: CampoGasto[] = [
  'fecha', 'proveedor', 'total', 'iva', 'base', 'ivaPct', 'numero', 'concepto', 'categoria', 'pagado', 'vence', 'tipo', 'local',
];

interface ReglaColumna {
  campo: DestinoColumna;
  /** Cabeceras exactas (normalizadas). */
  exactas?: string[];
  /** Palabras o frases que, si aparecen, la delatan. */
  contiene?: string[];
}

/** En orden: la primera que casa gana. Las específicas antes que las genéricas. */
const REGLAS: ReglaColumna[] = [
  { campo: 'ignorar', contiene: ['retencion', 'retenciones', 'irpf', 'isr retenido', 'cuenta contable', 'subcuenta', 'cuenta', 'recargo de equivalencia'] },
  { campo: 'vence', contiene: ['vencimiento', 'vence', 'fecha de vencimiento', 'due date', 'fecha limite'] },
  { campo: 'pagado', contiene: ['fecha de pago', 'fecha pago', 'pagado', 'pagada', 'ya lo pagaste', 'paid', 'estado de pago', 'estado del pago', 'condicion de pago', 'forma de pago', 'condicion'] },
  { campo: 'fecha', contiene: ['fecha', 'date', 'emision', 'dia'] },
  { campo: 'numero', exactas: ['n', 'no', 'num', 'n factura', 'no factura', 'num factura', 'numero', 'numero de factura', 'n de factura', 'factura', 'folio', 'serie y numero', 'documento'], contiene: ['folio', 'invoice', 'numero de factura', 'num factura', 'n factura', 'referencia', 'ref'] },
  { campo: 'proveedor', contiene: ['proveedor', 'razon social', 'emisor', 'supplier', 'vendor', 'acreedor', 'nombre'] },
  { campo: 'ivaPct', contiene: ['tipo de iva', 'tipo iva', 'porcentaje', 'tasa', 'iva pct', 'pct iva'] },
  { campo: 'base', contiene: ['base imponible', 'base', 'sin iva', 'subtotal', 'neto', 'importe neto', 'antes de impuestos'] },
  { campo: 'iva', contiene: ['cuota iva', 'iva trasladado', 'iva', 'igv', 'impuestos', 'impuesto', 'vat', 'tax', 'ieps'] },
  { campo: 'total', contiene: ['total', 'importe total', 'total factura', 'con iva', 'importe', 'monto', 'amount', 'cargo', 'a pagar'] },
  { campo: 'categoria', contiene: ['categoria', 'category', 'partida', 'tipo de gasto', 'clasificacion', 'rubro'] },
  { campo: 'tipo', contiene: ['que te dieron', 'tipo de documento', 'tipo doc', 'tipo'] },
  { campo: 'local', contiene: ['local', 'sucursal', 'centro', 'restaurante', 'sede', 'tienda'] },
  { campo: 'concepto', contiene: ['concepto', 'descripcion', 'detalle', 'concept', 'description', 'glosa', 'observaciones'] },
];

const VARIAS: ReadonlySet<DestinoColumna> = new Set(['iva', 'ignorar']);

function campoPorNombre(cabecera: string): DestinoColumna | undefined {
  const crudo = cabecera.trim();
  const h = normalizeText(crudo);
  if (!h) return undefined;
  // «IVA %» o «% IVA»: es el tipo, no el dinero. «IVA 21%» sí es dinero (una
  // columna por tipo, como en el libro de la gestoría).
  if (/%/.test(crudo) && /\biva|igv|vat\b/i.test(h) && !/\d/.test(crudo)) return 'ivaPct';
  const tokens = new Set(h.split(' '));
  for (const regla of REGLAS) {
    if (regla.exactas?.includes(h)) return regla.campo;
    if (regla.contiene?.some((w) => h === w || (w.includes(' ') ? ` ${h} `.includes(` ${w} `) : tokens.has(w)))) return regla.campo;
  }
  return undefined;
}

export interface ColumnasEntendidas {
  /** Destino de cada columna, en el orden del archivo. */
  columnas: DestinoColumna[];
  /** Columnas con nombre que no se usan («Base imponible», «Retención»…). */
  ignoradas: number[];
  /** Lo imprescindible que no aparece: hay que preguntar cuál es cuál. */
  faltan: Array<'fecha' | 'total'>;
}

const pareceFecha = (v: string) => Boolean(fechaDeCelda(v, true)) || /^\d{5}$/.test(v.trim());
const pareceDinero = (v: string) => parseImportMoney(v) !== undefined;

/**
 * Qué es cada columna, por su nombre y, si hace falta, por lo que lleva.
 * `muestra`: unas cuantas filas de datos (sin la cabecera).
 */
export function entenderColumnas(cabeceras: string[], muestra: string[][]): ColumnasEntendidas {
  const usados = new Set<DestinoColumna>();
  const columnas: DestinoColumna[] = cabeceras.map((c) => {
    const campo = campoPorNombre(c);
    if (!campo) return 'ignorar';
    if (!VARIAS.has(campo) && usados.has(campo)) return 'ignorar';
    usados.add(campo);
    return campo;
  });

  const valores = (i: number) => muestra.map((f) => String(f[i] ?? '').trim()).filter(Boolean);
  const libres = () => columnas.map((c, i) => (c === 'ignorar' && !campoPorNombre(cabeceras[i] ?? '') ? i : -1)).filter((i) => i >= 0);

  // Por contenido: la fecha (las fechas no se confunden con nada).
  if (!columnas.includes('fecha')) {
    const i = libres().find((j) => {
      const v = valores(j);
      return v.length > 0 && v.filter(pareceFecha).length / v.length >= 0.6;
    });
    if (i !== undefined) columnas[i] = 'fecha';
  }
  // Un IVA que son tipos (16, 21, 10…) y no dinero: es el %.
  columnas.forEach((c, i) => {
    if (c !== 'iva' || columnas.filter((x) => x === 'iva').length > 1) return;
    const v = valores(i).map((x) => parseImportMoney(x)).filter((x): x is number => x !== undefined);
    const tipos = new Set([0, 4, 5, 7, 8, 10, 12, 13, 15, 16, 18, 19, 21, 22, 23]);
    if (v.length > 0 && v.every((x) => tipos.has(x)) && (columnas.includes('total') || columnas.includes('base'))) {
      columnas[i] = columnas.includes('ivaPct') ? 'ignorar' : 'ivaPct';
    }
  });
  // El total por contenido solo si queda UNA columna de dinero sin dueño.
  if (!columnas.includes('total') && !columnas.includes('base')) {
    const dinero = libres().filter((j) => {
      const v = valores(j);
      return v.length > 0 && v.filter(pareceDinero).length / v.length >= 0.8 && !v.every(pareceFecha);
    });
    if (dinero.length === 1) columnas[dinero[0]] = 'total';
  }
  // Con total e IVA, la base sobra: se dice y no se usa.
  if (columnas.includes('total') && columnas.includes('iva')) {
    columnas.forEach((c, i) => {
      if (c === 'base') columnas[i] = 'ignorar';
    });
  }

  const ignoradas = columnas
    .map((c, i) => (c === 'ignorar' && String(cabeceras[i] ?? '').trim() !== '' && valores(i).length > 0 ? i : -1))
    .filter((i) => i >= 0);
  const faltan: Array<'fecha' | 'total'> = [];
  if (!columnas.includes('fecha')) faltan.push('fecha');
  if (!columnas.includes('total') && !columnas.includes('base')) faltan.push('total');
  return { columnas, ignoradas, faltan };
}

/* ------------------------------------------------------------------ */
/* Filas                                                               */
/* ------------------------------------------------------------------ */

/** Una fila de gasto, venga de la plantilla o de cualquier Excel. Todo texto. */
export interface FilaGasto {
  /** Fila del Excel (1 = primera fila de la hoja). */
  row: number;
  fecha: string;
  proveedor: string;
  /** Total con IVA (texto tal cual, o calculado con punto decimal). */
  total: string;
  /** IVA en dinero (vacío = sin IVA). */
  iva: string;
  categoria: string;
  pagado: string;
  vence: string;
  numero: string;
  concepto: string;
  tipo: string;
  local: string;
}

const dinero = (n: number) => round2(n).toFixed(2);

/**
 * Filas de cualquier Excel → `FilaGasto`, con las cuentas hechas: total =
 * base + IVA si no hay total; varias columnas de IVA se suman; base + % de
 * IVA calcula el IVA. `primeraFila`: número de fila del Excel de la primera
 * fila de datos.
 */
export function filasDeExcel(datos: string[][], columnas: DestinoColumna[], primeraFila = 2): FilaGasto[] {
  const de = (campo: CampoGasto) => columnas.map((c, i) => (c === campo ? i : -1)).filter((i) => i >= 0);
  const una = (fila: string[], campo: CampoGasto) => {
    const i = de(campo)[0];
    return i === undefined ? '' : String(fila[i] ?? '').trim();
  };
  const ivas = de('iva');
  const out: FilaGasto[] = [];
  datos.forEach((fila, n) => {
    if (fila.every((c) => String(c ?? '').trim() === '')) return;
    const totalRaw = una(fila, 'total');
    const baseRaw = una(fila, 'base');
    const pctRaw = una(fila, 'ivaPct');
    const ivaVals = ivas.map((i) => String(fila[i] ?? '').trim()).filter(Boolean);
    let iva: string = '';
    if (ivaVals.length === 1) iva = ivaVals[0];
    else if (ivaVals.length > 1) {
      const nums = ivaVals.map((v) => parseImportMoney(v));
      iva = nums.every((x) => x !== undefined) ? dinero(nums.reduce((a, b) => a! + b!, 0)!) : ivaVals.join(' + ');
    }
    let total = totalRaw;
    const base = parseImportMoney(baseRaw);
    let pct = parseImportMoney(pctRaw.replace('%', ''));
    if (pct !== undefined && pct > 0 && pct < 1) pct = round2(pct * 100);
    if (!iva && base !== undefined && pct !== undefined) iva = dinero((base * pct) / 100);
    if (!total && base !== undefined) {
      const ivaNum = iva ? parseImportMoney(iva) : 0;
      if (ivaNum !== undefined) total = dinero(base + ivaNum);
    }
    if (!iva && total && base !== undefined) {
      const t = parseImportMoney(total);
      if (t !== undefined && t - base > 0.004) iva = dinero(t - base);
    }
    out.push({
      row: primeraFila + n,
      fecha: una(fila, 'fecha'),
      proveedor: una(fila, 'proveedor'),
      total,
      iva,
      categoria: una(fila, 'categoria'),
      pagado: una(fila, 'pagado'),
      vence: una(fila, 'vence'),
      numero: una(fila, 'numero'),
      concepto: una(fila, 'concepto'),
      tipo: una(fila, 'tipo'),
      local: una(fila, 'local'),
    });
  });
  return out;
}

/* ------------------------------------------------------------------ */
/* Revisar                                                             */
/* ------------------------------------------------------------------ */

export type DudaGasto =
  | { kind: 'repetido'; mensaje: string }
  | { kind: 'categoria'; mensaje: string; raw: string }
  | { kind: 'fecha'; mensaje: string }
  | { kind: 'devolucion'; mensaje: string }
  | { kind: 'inversion'; mensaje: string }
  | { kind: 'local'; mensaje: string; raw: string }
  | { kind: 'iva'; mensaje: string };

/** Lo que el usuario contestó en «Antes de guardar», por fila del Excel. */
export interface CorreccionGasto {
  /** Etiqueta elegida («Materia prima › Comida»). */
  categoria?: string;
  /** «Usar esta categoría siempre para este proveedor» (marcada por defecto). */
  siempre?: boolean;
  locationId?: string;
  repetido?: 'misma' | 'otra';
  fecha?: 'ok';
  devolucion?: 'si' | 'no';
  inversion?: 'si' | 'no';
  iva?: 'ok';
}
export type CorreccionesGastos = Record<number, CorreccionGasto>;

export interface GastoLeido extends ExpenseImportItem {
  /** Total CON IVA. */
  total: number;
  iva: number;
  categoria?: CategoriaLeida;
  supplierName?: string;
  esNota: boolean;
  /** Dudas sin resolver: con alguna, se guarda «Por confirmar». */
  dudas: DudaGasto[];
}

export interface GastosGuardados {
  supplierId?: string;
  invoiceNumber?: string;
  netAmount: number;
  date: string;
  vatPct: number;
  taxAmount?: number;
  invoiceTotal?: number;
  documentType?: PurchaseDocumentType;
}

export interface RevisarGastosOptions {
  /** Local de los gastos que no dicen otro (el elegido arriba). */
  defaultLocationId: string;
  /** Locales activos. Con uno solo, la columna «Local» no se mira. */
  locations: Array<{ locationId: string; name: string }>;
  suppliers: Array<{ supplierId: string; name: string; lastCategory?: ExpenseCategory; lastSubgroup?: string; creditDays?: number }>;
  subgrupos?: TenantSettings['expenseSubgroups'];
  /** Categoría que suele llevar cada proveedor (`categoriasPorProveedor`). */
  historico?: Map<string, ExpenseCategory>;
  /** Gastos ya guardados, para avisar de los repetidos. */
  guardados?: GastosGuardados[];
  /** Hoy (para las fechas futuras). */
  hoy?: string;
  /** Cómo leer 03/04 si nada lo aclara. */
  diaPrimero?: boolean;
  /** Para el fijo/variable de cada categoría. */
  settings?: Pick<TenantSettings, 'expenseCategoryNature'>;
}

export interface NoSeGuarda {
  row: number;
  name: string;
  message: string;
}

export interface LoQueEntra {
  porGrupo: Record<GrupoGasto, number>;
  proveedoresNuevos: number;
  porPagar: number;
  venceMasProximo?: string;
  notas: number;
}

export interface RevisionGastos {
  listos: GastoLeido[];
  /** Con dudas: se arreglan aquí; los que no, se guardan «Por confirmar». */
  porRevisar: GastoLeido[];
  /** «Mandarlo a Inversiones»: no se guardan como gasto. */
  aInversiones: GastoLeido[];
  /** Ni se pueden guardar (sin fecha o sin total legible) ni lo dijo el usuario. */
  noSeGuardan: NoSeGuarda[];
  /** «Es la misma: no guardarla», «Sí, es una devolución». */
  descartados: NoSeGuarda[];
  newSuppliers: string[];
  loQueEntra: LoQueEntra;
  /** Total con IVA y sin IVA de lo que se guarda (listos + por revisar). */
  total: number;
  totalNet: number;
  /** Filas con datos. */
  filas: number;
  /** Proveedor → categoría que el usuario quiere siempre. */
  categoriaSiempre: Array<{ supplierName: string; supplierId?: string; category: ExpenseCategory; subgroup?: string }>;
}

/** Palabras de la columna «Local» que quieren decir «se reparte entre todos». */
const TODOS = new Set(['todos', 'todas', 'grupo', 'comun', 'todos los locales', 'general', 'repartido', 'repartir']);

/**
 * Categoría segura sin que la diga el Excel (regla 3): la del proveedor si ya
 * existe, por palabras del concepto o por el nombre del proveedor. Sin pistas
 * claras, undefined: va a revisar (nunca se supone «materia prima»).
 */
export function categoriaSegura(
  proveedor: string,
  concepto: string,
  options: Pick<RevisarGastosOptions, 'historico' | 'suppliers'>,
  supplierId?: string,
): { category: ExpenseCategory; subgroup?: string } | undefined {
  const deFicha = supplierId ? options.suppliers.find((s) => s.supplierId === supplierId) : undefined;
  if (deFicha?.lastCategory) return { category: visibleCategory(deFicha.lastCategory), subgroup: deFicha.lastSubgroup };
  const nombre = normalizeText(proveedor);
  if (nombre && options.historico?.has(nombre)) return { category: visibleCategory(options.historico.get(nombre)!) };
  const porConcepto = parseExpenseCategory(concepto);
  if (porConcepto) return { category: visibleCategory(porConcepto) };
  const porEscaner = adivinarCategoria({ vendorName: proveedor ? { value: proveedor } : undefined, rawText: concepto });
  if (porEscaner.categoria !== 'compras_materia_prima') return { category: visibleCategory(porEscaner.categoria) };
  const porNombre = parseExpenseCategory(proveedor);
  if (porNombre) return { category: visibleCategory(porNombre) };
  return undefined;
}

const esNotaTexto = (t: string) => /^(nota|ticket|tique|recibo|remision|remito|albaran|guia)/.test(normalizeText(t));
const esFacturaTexto = (t: string) => /^(factura|cfdi|invoice)/.test(normalizeText(t));

/**
 * Las filas → lo listo, lo que hay que revisar y lo que no entra. Nada se
 * guarda aquí: es la previa de «Antes de guardar».
 */
export function revisarGastos(
  filas: FilaGasto[],
  options: RevisarGastosOptions,
  correcciones: CorreccionesGastos = {},
): RevisionGastos {
  const hoy = options.hoy ?? new Date().toISOString().slice(0, 10);
  const listos: GastoLeido[] = [];
  const porRevisar: GastoLeido[] = [];
  const aInversiones: GastoLeido[] = [];
  const noSeGuardan: NoSeGuarda[] = [];
  const descartados: NoSeGuarda[] = [];
  const categoriaSiempre: RevisionGastos['categoriaSiempre'] = [];
  const proveedorPorNombre = new Map(options.suppliers.map((s) => [normalizeText(s.name), s]));
  const localPorNombre = new Map(options.locations.map((l) => [normalizeText(l.name), l.locationId]));
  const multiLocal = options.locations.length > 1;
  const nuevos = new Map<string, string>();
  const diaPrimero = options.diaPrimero ?? detectDayFirst(filas.map((f) => f.fecha));

  // Repetidos: mismo nº del mismo proveedor, o mismo proveedor + total + fecha.
  const porNumero = new Map<string, string>();
  const porTotalFecha = new Map<string, string>();
  for (const g of options.guardados ?? []) {
    if (g.documentType === 'albaran' || !g.supplierId) continue;
    const total = Math.round(totalDeGasto(g) * 100);
    const numero = normalizeText(g.invoiceNumber ?? '').replace(/ /g, '');
    const quien = `ya tienes esta factura (${g.invoiceNumber ? `${g.invoiceNumber}, ` : ''}${g.date}, mismo total)`;
    if (numero) porNumero.set(`${g.supplierId}|${numero}`, quien);
    porTotalFecha.set(`${g.supplierId}|${total}|${g.date}`, quien);
  }

  for (const f of filas) {
    const quien = f.proveedor || f.concepto || `Fila ${f.row}`;
    const c = correcciones[f.row] ?? {};
    const noVa = (message: string) => noSeGuardan.push({ row: f.row, name: quien, message });

    const date = fechaDeCelda(f.fecha, diaPrimero);
    if (!date) {
      noVa(f.fecha ? `No entiendo la fecha «${f.fecha.slice(0, 30)}». Escríbela como 05/10/2026.` : 'Falta la fecha.');
      continue;
    }
    let total = parseImportMoney(f.total);
    if (total === undefined) {
      noVa(f.total ? `No entiendo el total «${f.total.slice(0, 30)}».` : 'Falta el total.');
      continue;
    }
    if (total === 0) {
      descartados.push({ row: f.row, name: quien, message: 'El total es 0: no se guarda.' });
      continue;
    }
    const dudas: DudaGasto[] = [];
    if (total < 0) {
      if (c.devolucion === 'si') {
        descartados.push({ row: f.row, name: quien, message: 'Devolución: todavía no se guardan. Descuéntala del gasto que corrige.' });
        continue;
      }
      total = -total;
      if (c.devolucion !== 'no') dudas.push({ kind: 'devolucion', mensaje: 'El total es negativo. ¿Es una devolución?' });
    }

    const ivaEnPct = /%/.test(f.iva);
    let iva = f.iva === '' ? 0 : parseImportMoney(f.iva.replace('%', ''));
    if (iva !== undefined && ivaEnPct && iva > 0 && iva <= 30) {
      const pct = iva;
      iva = round2(total - total / (1 + pct / 100));
      if (c.iva !== 'ok') dudas.push({ kind: 'iva', mensaje: `El IVA venía en % (${pct} %), no en dinero. Lo calculé: ${iva.toFixed(2)}.` });
    }
    if (iva === undefined) {
      noVa(`No entiendo el IVA «${f.iva.slice(0, 30)}». Va en dinero: la línea «IVA» de la factura.`);
      continue;
    }
    iva = Math.abs(iva);
    if (iva > 0 && iva >= total - iva) {
      // ¿Escribieron el % en vez del dinero? Si parece un tipo, se toma así.
      const pct = iva;
      if (pct <= 30) {
        iva = round2(total - total / (1 + pct / 100));
        if (c.iva !== 'ok') dudas.push({ kind: 'iva', mensaje: `El IVA (${pct}) parece un %, no dinero. Lo tomé como ${pct} %: ${iva.toFixed(2)}.` });
      } else {
        noVa(`El IVA (${pct}) es más que lo que queda sin IVA. ¿Escribiste el % en vez del dinero?`);
        continue;
      }
    }
    const base = round2(total - iva);
    const vatPct = iva > 0 ? Math.round((iva / base) * 100 * 10_000) / 10_000 : 0;

    if (date > hoy && c.fecha !== 'ok') dudas.push({ kind: 'fecha', mensaje: `La fecha (${date}) es futura. ¿Está bien?` });
    else if (date < sumarDias(hoy, -3 * 365) && c.fecha !== 'ok') dudas.push({ kind: 'fecha', mensaje: `La fecha (${date}) es de hace más de tres años. ¿Está bien?` });

    // Proveedor.
    const existente = f.proveedor ? proveedorPorNombre.get(normalizeText(f.proveedor)) : undefined;
    let newSupplierName: string | undefined;
    if (f.proveedor && !existente) {
      newSupplierName = nuevos.get(normalizeText(f.proveedor)) ?? f.proveedor.slice(0, 120);
      nuevos.set(normalizeText(f.proveedor), newSupplierName);
    }
    const supplierKey = existente?.supplierId ?? (f.proveedor ? `nuevo:${normalizeText(f.proveedor)}` : undefined);

    // Categoría (regla 3).
    let categoria: CategoriaLeida | undefined;
    const contexto = { proveedor: f.proveedor, concepto: f.concepto, subgrupos: options.subgrupos, historico: options.historico };
    if (c.categoria) categoria = leerCategoriaPlantilla(c.categoria, contexto);
    else if (f.categoria.trim()) {
      categoria = leerCategoriaPlantilla(f.categoria, contexto);
      if (!categoria) dudas.push({ kind: 'categoria', mensaje: `No sé en qué categoría va. Dice «${f.categoria.slice(0, 40)}».`, raw: f.categoria });
    } else {
      const segura = categoriaSegura(f.proveedor, f.concepto, options, existente?.supplierId);
      if (segura) categoria = { category: segura.category, ...(segura.subgroup ? { subgroup: segura.subgroup } : {}), label: etiquetaDeCategoria(segura.category, segura.subgroup), deducida: true };
      else dudas.push({ kind: 'categoria', mensaje: `No sé en qué categoría va${f.concepto ? `. Dice «${f.concepto.slice(0, 40)}»` : ''}.`, raw: '' });
    }
    if (c.categoria && categoria && c.siempre !== false && f.proveedor) {
      categoriaSiempre.push({ supplierName: f.proveedor, supplierId: existente?.supplierId, category: categoria.category, subgroup: categoria.subgroup });
    }
    const category: ExpenseCategory = categoria?.category ?? 'otros_gastos';

    // Local (regla 9).
    let locationId = options.defaultLocationId;
    if (multiLocal && f.local) {
      const key = normalizeText(f.local);
      if (c.locationId) locationId = c.locationId;
      else if (TODOS.has(key)) locationId = SHARED_LOCATION_ID;
      else if (localPorNombre.has(key)) locationId = localPorNombre.get(key)!;
      else dudas.push({ kind: 'local', mensaje: `No tienes un local que se llame «${f.local.slice(0, 40)}». ¿Cuál es?`, raw: f.local });
    } else if (c.locationId) locationId = c.locationId;

    // Repetidos (dentro del archivo y contra Alexia).
    const numero = f.numero ? f.numero.slice(0, 40) : undefined;
    if (supplierKey && c.repetido !== 'otra') {
      const numeroLimpio = numero ? normalizeText(numero).replace(/ /g, '') : '';
      const claveNumero = numeroLimpio ? `${supplierKey}|${numeroLimpio}` : undefined;
      const claveTotal = `${supplierKey}|${Math.round(total * 100)}|${date}`;
      const antes = (claveNumero && porNumero.get(claveNumero)) || porTotalFecha.get(claveTotal);
      if (antes) {
        if (c.repetido === 'misma') {
          descartados.push({ row: f.row, name: quien, message: 'Dijiste que es la misma: no se guarda.' });
          continue;
        }
        dudas.push({ kind: 'repetido', mensaje: `Parece repetido: ${antes}. Si la guardas, contaría doble.` });
      }
      if (claveNumero && !porNumero.has(claveNumero)) porNumero.set(claveNumero, `está también en la fila ${f.row}`);
      if (!porTotalFecha.has(claveTotal)) porTotalFecha.set(claveTotal, `está también en la fila ${f.row}`);
    }

    // ¿Inversión? (regla 13 de Gastos).
    const palabra = pareceInversion(`${f.concepto} ${categoria ? '' : f.categoria}`) ?? pareceInversion(f.proveedor);
    if (palabra && c.inversion !== 'no' && c.inversion !== 'si') {
      dudas.push({ kind: 'inversion', mensaje: '¿Es un gasto o compraste equipo? Algo así suele ir a Inversiones, no a gastos del mes.' });
    }

    // Nota o factura (regla 5).
    // Una nómina sin IVA ni número no es una nota: nunca tiene factura (regla 6).
    const esNota = esNotaTexto(f.tipo) || (!esFacturaTexto(f.tipo) && iva === 0 && !numero && !CATEGORIAS_SIN_FACTURA.has(category));

    // Pagado y vencimiento.
    const pagadoTexto = f.pagado.trim();
    let pagado = parseImportBoolean(pagadoTexto);
    if (pagado === undefined && pagadoTexto) {
      const tipoPago = parseImportPaymentType(pagadoTexto);
      if (tipoPago) pagado = tipoPago === 'contado';
      else if (fechaDeCelda(pagadoTexto, diaPrimero)) pagado = true;
    }
    if (pagado === undefined) pagado = true;
    const vence = f.vence ? fechaDeCelda(f.vence, diaPrimero) : undefined;
    const dias = existente?.creditDays;
    const dueDate = pagado ? undefined : vence && vence >= date ? vence : dias != null ? sumarDias(date, dias) : date;

    const input: ExpenseInput = {
      locationId,
      paymentType: pagado ? 'contado' : 'credito',
      date,
      dueDate,
      paymentDate: pagado ? date : undefined,
      concept: (f.concepto || f.proveedor || CATEGORY_LABELS[category]).slice(0, 160),
      supplierId: existente?.supplierId,
      invoiceNumber: numero,
      category,
      subgroup: categoria?.subgroup ? categoria.subgroup.slice(0, 40) : undefined,
      nature: naturalezaDe(category, options.settings),
      netAmount: base,
      vatPct,
      ...(iva > 0 ? { taxAmount: round2(iva) } : {}),
      invoiceTotal: round2(total),
      paid: pagado,
      documentType: esNota ? 'albaran' : 'factura',
      invoiceStatus: esNota ? 'pendiente' : iva === 0 && !numero ? 'sin_factura' : 'facturado',
    };
    const valido = expenseInputSchema.safeParse(input);
    if (!valido.success) {
      noVa(valido.error.issues[0]?.message ?? 'Hay un dato que no es válido.');
      continue;
    }
    const item: GastoLeido = {
      input,
      row: f.row,
      newSupplierName,
      categoryGuessed: Boolean(categoria?.deducida),
      total: round2(total),
      iva: round2(iva),
      categoria,
      supplierName: f.proveedor || undefined,
      esNota,
      dudas,
    };
    if (c.inversion === 'si') aInversiones.push(item);
    else if (dudas.length > 0) porRevisar.push(item);
    else listos.push(item);
  }

  const seGuardan = [...listos, ...porRevisar];
  const porGrupo: Record<GrupoGasto, number> = { materia_prima: 0, personal: 0, generales: 0, financieros: 0 };
  for (const i of seGuardan) porGrupo[grupoDeGasto(i.input.category)] += i.input.netAmount;
  for (const k of Object.keys(porGrupo) as GrupoGasto[]) porGrupo[k] = round2(porGrupo[k]);
  const debidos = seGuardan.filter((i) => !i.input.paid);
  const venceMasProximo = debidos.map((i) => i.input.dueDate!).filter(Boolean).sort().find((d) => d >= hoy) ?? debidos.map((i) => i.input.dueDate!).sort()[0];
  const newSuppliers = [...nuevos.values()].filter((n) => seGuardan.some((i) => i.newSupplierName === n) || aInversiones.some((i) => i.newSupplierName === n));
  return {
    listos,
    porRevisar,
    aInversiones,
    noSeGuardan,
    descartados,
    newSuppliers,
    loQueEntra: {
      porGrupo,
      proveedoresNuevos: newSuppliers.length,
      porPagar: debidos.length,
      ...(venceMasProximo ? { venceMasProximo } : {}),
      notas: seGuardan.filter((i) => i.esNota).length,
    },
    total: round2(seGuardan.reduce((acc, i) => acc + i.total, 0)),
    totalNet: round2(seGuardan.reduce((acc, i) => acc + i.input.netAmount, 0)),
    filas: filas.length,
    categoriaSiempre,
  };
}

/* ------------------------------------------------------------------ */
/* Extracto del banco (revisión de Alex del 14 oct 2026)                */
/* ------------------------------------------------------------------ */

/** Lo que dice Alexia cuando le suben un extracto del banco. */
export const MENSAJE_EXTRACTO_BANCO = 'Esto parece un extracto del banco. No lo leo: sube el Excel de tus facturas o el de tu contador.';

/**
 * ¿Es un extracto del banco? Decidido: no se suben extractos (los negativos
 * se descartaban y los ingresos entraban como gastos). Lo delata:
 *  - una columna «Saldo», o «Cargo» y «Abono» (o «Debe» y «Haber»), o
 *  - casi todo (≥ 80 %) en negativo en la columna del dinero.
 */
export function pareceExtractoBancario(cabeceras: string[], datos: string[][]): boolean {
  const nombres = cabeceras.map((c) => normalizeText(c));
  const tiene = (palabra: string) => nombres.some((n) => n === palabra || n.split(' ').includes(palabra));
  if (tiene('saldo') || tiene('saldos')) return true;
  if ((tiene('cargo') || tiene('cargos')) && (tiene('abono') || tiene('abonos'))) return true;
  if (tiene('debe') && tiene('haber')) return true;
  // Casi todo en negativo: la columna del dinero (por su nombre o la única que lo parece).
  const { columnas } = entenderColumnas(cabeceras, datos.slice(0, 50));
  let i = columnas.indexOf('total');
  if (i < 0) i = columnas.indexOf('base');
  if (i < 0) return false;
  const valores = datos.slice(0, 200).map((f) => parseImportMoney(String(f[i] ?? '').trim())).filter((v): v is number => v !== undefined && v !== 0);
  if (valores.length < 3) return false;
  return valores.filter((v) => v < 0).length / valores.length >= 0.8;
}

/**
 * «Importar 0 gastos» (14 oct 2026): por qué no entra ninguna fila, en una
 * frase. Lo usa la pantalla para no dejar solo un botón apagado y llevar a
 * emparejar las columnas.
 */
export function porQueNoEntraNada(revision: Pick<RevisionGastos, 'noSeGuardan' | 'descartados'>): { faltaFecha: boolean; faltaTotal: boolean; motivo: string } {
  const todas = [...revision.noSeGuardan, ...revision.descartados];
  const sinFecha = todas.filter((n) => /fecha/i.test(n.message)).length;
  const sinTotal = todas.filter((n) => /total/i.test(n.message)).length;
  const faltaFecha = todas.length === 0 || sinFecha >= todas.length / 2;
  const faltaTotal = todas.length === 0 || sinTotal >= todas.length / 2 || !faltaFecha;
  const motivo = faltaFecha && faltaTotal
    ? 'No encuentro la fecha ni el total. ¿Cuál es cada columna?'
    : faltaFecha
      ? 'No encuentro la fecha en ninguna fila. ¿Cuál es la columna de la fecha?'
      : 'No encuentro el total en ninguna fila. ¿Cuál es la columna del total?';
  return { faltaFecha, faltaTotal, motivo };
}

/** El motivo que se guarda en «Por confirmar» (corto, para la lista). */
export function motivoPorConfirmar(dudas: DudaGasto[]): string {
  return dudas.map((d) => d.mensaje).join(' ').slice(0, 300);
}

/* ------------------------------------------------------------------ */
/* La plantilla de Alex                                                */
/* ------------------------------------------------------------------ */

/**
 * Plantilla de gastos (Plantilla-gastos-Alexia.xlsx) → filas. Columnas fijas:
 * Fecha · Proveedor · Total · IVA · Categoría · ¿Ya lo pagaste? · Vence el ·
 * Nº de factura · Concepto · ¿Qué te dieron? · Local. El «Ejemplo:» va en el
 * proveedor.
 */
export function filasDePlantillaGastos(raw: string[][], det: PlantillaDetectada): FilaGasto[] {
  return filasDePlantilla(raw, det, 1).map(({ row, cells }) => {
    const [fecha = '', proveedor = '', total = '', iva = '', categoria = '', pagado = '', vence = '', numero = '', concepto = '', tipo = '', local = ''] = cells;
    return { row, fecha, proveedor, total, iva, categoria, pagado, vence, numero, concepto, tipo, local };
  });
}

/** La plantilla, revisada como cualquier otro Excel. */
export function buildGastosPlantilla(
  raw: string[][],
  det: PlantillaDetectada,
  options: RevisarGastosOptions,
  correcciones: CorreccionesGastos = {},
): RevisionGastos {
  return revisarGastos(filasDePlantillaGastos(raw, det), { diaPrimero: true, ...options }, correcciones);
}
